import { useSprintPurposeConfigs, useSprintActivityTypeConfigs } from '../../../../apiLayer/queryHooks/queries'
import { ICON_MAP } from '../constants/iconMap'
import { setActivityTemplates } from '../constants/activities'
import type { Activity, PurposeOption } from '../types'
import { ActivityType } from 'pure-js/enums/ActivityType'
import type { SprintPurposeConfig } from 'pure-js/types/SprintPurposeConfig'
import type { ActivityTypeConfig } from 'pure-js/types/ActivityTypeConfig'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import type { TranslationFunction } from '../../../shared/helpers/liveActivityHelpers'

/** Format total time in minutes as hours for facilitator display (BO keeps minutes). */
function formatTotalTimeMinutesAsHours(totalTimeMinutes: number): string {
  const hours = totalTimeMinutes / 60
  const label = hours % 1 === 0 ? `${hours} h` : `${hours.toFixed(1)} h`
  return `Total time ${label}`
}

function toPurposeOption(config: SprintPurposeConfig): PurposeOption {
  return {
    id: config.purposeKey,
    title: config.titleKey,
    why: config.whyKey,
    forWhom: config.forWhomKey,
    learningGoals: config.learningGoalsKey,
    tooltip: config.tooltipKey,
    icon: ICON_MAP[config.iconName],
    metrics: {
      totalTime: formatTotalTimeMinutesAsHours(config.metrics.totalTimeMinutes),
      sprintDuration: `${config.metrics.numberOfSessions} session${config.metrics.numberOfSessions !== 1 ? 's' : ''}`,
      activities: `${config.metrics.numberOfActivities} activities`
    }
  }
}

/** Maps BO activity type config to wizard Activity (includes slotDisplayBufferMinutes for display only; game receives slot only). */
function toActivity(config: ActivityTypeConfig, t: TranslationFunction): Activity {
  return {
    id: `${config.activityType}-1`,
    type: config.activityType as ActivityType,
    icon: ICON_MAP[config.iconName],
    title: t(config.titleKey),
    description: t(config.descriptionKey),
    duration: config.defaultDurationMinutes,
    numberOfLoops: config.defaultNumberOfLoops ?? undefined,
    customSlotLength: config.defaultSlotLengthSeconds ?? undefined,
    slotDisplayBufferMinutes: config.slotDisplayBufferMinutes ?? undefined,
    limit: config.limit,
    isFixed: false
  }
}

export function useSprintWizardConfig() {
  const { t } = useTranslation()
  const tCompat = t as (key: string, defaultValue?: string) => string
  const { data: purposeRes, isLoading: pLoading } = useSprintPurposeConfigs()
  const { data: activityRes, isLoading: aLoading } = useSprintActivityTypeConfigs()

  const isLoading = pLoading || aLoading

  const purposes: PurposeOption[] = purposeRes?.data?.length ? purposeRes.data.map(toPurposeOption) : []

  const availableActivities: Activity[] = activityRes?.data?.length
    ? activityRes.data.map((cfg) => toActivity(cfg, tCompat))
    : []

  // Keep the module-level cache in sync so getActivityTemplateByType works in transformers
  if (availableActivities.length) {
    setActivityTemplates(availableActivities)
  }

  const orderedTypesByPurpose: Record<string, Array<{ type: ActivityType; isFixed?: boolean }>> = purposeRes?.data
    ?.length
    ? Object.fromEntries(
        purposeRes.data.map((cfg) => [
          cfg.purposeKey,
          cfg.defaultActivities.map((da) => ({ type: da.activityType as ActivityType, isFixed: da.isFixed }))
        ])
      )
    : {}

  return { purposes, availableActivities, orderedTypesByPurpose, isLoading }
}
